import {
  useEffect,
  useState,
  useCallback,
  useId,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  BarcodeFormat,
  DecodeHintType,
  BinaryBitmap,
  HybridBinarizer,
  MultiFormatReader,
  HTMLCanvasElementLuminanceSource,
} from "@zxing/library";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Alert, AlertDescription } from "@/shared/ui/alert";
import { Button } from "@/shared/components/atoms/button";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { looksLikeShortId } from "@/shared/utils/shortId";
import { shortIdsService } from "@/shared/services/short-ids.service";

export type ScannerMode =
  | "qr-only"
  | "code128-only"
  | "qr-and-code128"
  | "multi";

interface BarcodeScannerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onScanResult?: (value: string) => void;
  readerId?: string;
  mode?: ScannerMode;
  title?: string;
  instruction?: string;
  headerAccessory?: ReactNode;
}

type MediaSettingsRangeLike = {
  min?: number;
  max?: number;
  step?: number;
};

type ExtendedMediaTrackCapabilities = MediaTrackCapabilities & {
  focusMode?: string[];
  zoom?: MediaSettingsRangeLike;
  torch?: boolean;
};

type ExtendedMediaTrackSettings = MediaTrackSettings & {
  zoom?: number;
};

const CAMERA_INIT_DELAY_MS = 300;
const DUPLICATE_RESULT_WINDOW_MS = 3000;
const REQUIRED_MATCHES = 2;
const BACK_CAMERA_LABEL_RE = /back|rear|environment|traseira|trasera|arriere/i;
const AUTO_ZOOM_FACTOR = 2.0;
const SCAN_INTERVAL_MS = 150;
const CROP_RATIOS = [1.0, 0.65, 0.40, 0.25];

const normalizeScannedValue = (value: string) => value.trim();

const getFormatsForMode = (mode: ScannerMode): BarcodeFormat[] => {
  switch (mode) {
    case "qr-only":
      return [BarcodeFormat.QR_CODE];
    case "code128-only":
      return [BarcodeFormat.CODE_128];
    case "qr-and-code128":
      return [BarcodeFormat.QR_CODE, BarcodeFormat.CODE_128];
    case "multi":
      return [
        BarcodeFormat.QR_CODE,
        BarcodeFormat.CODE_128,
        BarcodeFormat.EAN_13,
        BarcodeFormat.EAN_8,
        BarcodeFormat.UPC_A,
        BarcodeFormat.UPC_E,
        BarcodeFormat.CODE_39,
        BarcodeFormat.CODE_93,
        BarcodeFormat.ITF,
        BarcodeFormat.CODABAR,
      ];
    default:
      return [BarcodeFormat.QR_CODE];
  }
};

const createReader = (mode: ScannerMode): MultiFormatReader => {
  const hints = new Map<DecodeHintType, unknown>([
    [DecodeHintType.POSSIBLE_FORMATS, getFormatsForMode(mode)],
    [DecodeHintType.TRY_HARDER, true],
    [DecodeHintType.CHARACTER_SET, "UTF-8"],
  ]);
  const reader = new MultiFormatReader();
  reader.setHints(hints);
  return reader;
};

const buildVideoConstraints = (
  preferredCameraId?: string | null,
  fallback = false,
): MediaStreamConstraints => {
  const base: MediaTrackConstraints = {
    width: { ideal: 1920 },
    height: { ideal: 1080 },
    frameRate: { ideal: 30 },
  };

  if (fallback) {
    return { audio: false, video: { ...base, facingMode: { ideal: "environment" } } };
  }

  if (preferredCameraId) {
    return { audio: false, video: { ...base, deviceId: { exact: preferredCameraId } } };
  }

  return { audio: false, video: { ...base, facingMode: "environment" } };
};

function decodeCrop(
  reader: MultiFormatReader,
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  cropRatio: number,
): string | null {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (vw === 0 || vh === 0) return null;

  const cw = Math.round(vw * cropRatio);
  const ch = Math.round(vh * cropRatio);
  const sx = Math.round((vw - cw) / 2);
  const sy = Math.round((vh - ch) / 2);

  canvas.width = cw;
  canvas.height = ch;

  ctx.drawImage(video, sx, sy, cw, ch, 0, 0, cw, ch);

  const source = new HTMLCanvasElementLuminanceSource(canvas);
  const bitmap = new BinaryBitmap(new HybridBinarizer(source));

  try {
    const result = reader.decode(bitmap);
    return result?.getText() ?? null;
  } catch {
    return null;
  }
}

export const BarcodeScannerDialog = ({
  open,
  onOpenChange,
  onScanResult,
  readerId,
  mode = "qr-only",
  title,
  instruction,
  headerAccessory,
}: BarcodeScannerDialogProps) => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();

  const generatedReaderId = useId();
  const effectiveReaderId = useMemo(
    () =>
      readerId ??
      `qr-reader-${generatedReaderId.replace(/[^a-zA-Z0-9_-]/g, "")}`,
    [generatedReaderId, readerId],
  );

  const [permissionDenied, setPermissionDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [isStarting, setIsStarting] = useState(false);

  const [zoomSupported, setZoomSupported] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [zoomRange, setZoomRange] = useState({ min: 1, max: 1, step: 0.1 });

  const [torchSupported, setTorchSupported] = useState(false);
  const [torchOn, setTorchOn] = useState(false);

  const [hasEverShownZoom, setHasEverShownZoom] = useState(false);
  const [hasEverShownTorch, setHasEverShownTorch] = useState(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const initTimeoutRef = useRef<number | null>(null);
  const cancelledRef = useRef(false);
  const handlingSuccessRef = useRef(false);
  const restartKeyRef = useRef(0);
  const onScanSuccessRef = useRef<(text: string) => void>(() => {});
  const scanTimerRef = useRef<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const readerInstanceRef = useRef<MultiFormatReader | null>(null);
  const modeRef = useRef<ScannerMode>(mode);
  modeRef.current = mode;
  const detectionRef = useRef<{
    value: string | null;
    count: number;
    lastAt: number;
  }>({ value: null, count: 0, lastAt: 0 });

  const resetScannerUiState = useCallback(() => {
    setZoomSupported(false);
    setZoomLevel(1);
    setZoomRange({ min: 1, max: 1, step: 0.1 });
    setTorchSupported(false);
    setTorchOn(false);
    setHasEverShownZoom(false);
    setHasEverShownTorch(false);
  }, []);

  const getActiveTrack = useCallback((): MediaStreamTrack | null => {
    return streamRef.current?.getVideoTracks?.()[0] ?? null;
  }, []);

  const stopTracks = useCallback(() => {
    const stream = streamRef.current;
    streamRef.current = null;

    if (stream) {
      for (const track of stream.getTracks()) {
        try {
          track.stop();
        } catch {
          // ignore
        }
      }
    }

    const video = videoRef.current;
    if (video) {
      try {
        video.pause();
      } catch {
        // ignore
      }
      video.srcObject = null;
    }
  }, []);

  const syncCameraSummary = useCallback(() => {
    // camera summary available via track.getSettings() if needed in future
  }, []);

  const syncTrackCapabilities = useCallback(() => {
    const track = getActiveTrack();
    if (!track) {
      setZoomSupported(false);
      setTorchSupported(false);
      return;
    }

    try {
      const settings = track.getSettings() as ExtendedMediaTrackSettings;
      const capabilities = track.getCapabilities() as ExtendedMediaTrackCapabilities;

      if (
        capabilities.zoom &&
        typeof capabilities.zoom.min === "number" &&
        typeof capabilities.zoom.max === "number"
      ) {
        const supported = capabilities.zoom.max > capabilities.zoom.min;
        setZoomSupported(supported);
        if (supported) {
          setHasEverShownZoom(true);
        }
        setZoomRange({
          min: capabilities.zoom.min,
          max: capabilities.zoom.max,
          step: capabilities.zoom.step || 0.1,
        });
        setZoomLevel(
          typeof settings.zoom === "number"
            ? settings.zoom
            : capabilities.zoom.min,
        );
      } else {
        setZoomSupported(false);
      }

      const torchCapable = Boolean(capabilities.torch);
      setTorchSupported(torchCapable);
      if (torchCapable) {
        setHasEverShownTorch(true);
      }
    } catch {
      setZoomSupported(false);
      setTorchSupported(false);
    }
  }, [getActiveTrack]);

  const applyZoom = useCallback(
    async (nextZoom: number) => {
      const track = getActiveTrack();
      if (!track) return;

      try {
        const capabilities = track.getCapabilities() as ExtendedMediaTrackCapabilities;
        if (!capabilities.zoom) return;

        const min = capabilities.zoom.min ?? 1;
        const max = capabilities.zoom.max ?? 1;
        const clamped = Math.min(max, Math.max(min, nextZoom));

        await track.applyConstraints({
          advanced: [{ zoom: clamped } as MediaTrackConstraintSet],
        });

        setZoomLevel(clamped);
      } catch {
        // best effort only
      }
    },
    [getActiveTrack],
  );

  const applyTorch = useCallback(
    async (enabled: boolean) => {
      const track = getActiveTrack();
      if (!track) return;

      try {
        const capabilities = track.getCapabilities() as ExtendedMediaTrackCapabilities;
        if (!capabilities.torch) return;

        await track.applyConstraints({
          advanced: [{ torch: enabled } as MediaTrackConstraintSet],
        });

        setTorchOn(enabled);
      } catch {
        // best effort only
      }
    },
    [getActiveTrack],
  );

  const applyBestTrackSettings = useCallback(async () => {
    const track = getActiveTrack();
    if (!track) return;

    try {
      const capabilities = track.getCapabilities() as ExtendedMediaTrackCapabilities;
      const advanced: MediaTrackConstraintSet[] = [];

      if (
        Array.isArray(capabilities.focusMode) &&
        capabilities.focusMode.includes("continuous")
      ) {
        advanced.push({ focusMode: "continuous" } as MediaTrackConstraintSet);
      }

      if (
        capabilities.zoom &&
        typeof capabilities.zoom.min === "number" &&
        typeof capabilities.zoom.max === "number" &&
        capabilities.zoom.max > capabilities.zoom.min
      ) {
        const desiredZoom = Math.min(AUTO_ZOOM_FACTOR, capabilities.zoom.max);
        advanced.push({ zoom: desiredZoom } as MediaTrackConstraintSet);
        setZoomLevel(desiredZoom);
      }

      if (advanced.length > 0) {
        await track.applyConstraints({ advanced });
      }
    } catch {
      // best effort only
    }

    syncTrackCapabilities();
  }, [getActiveTrack, syncTrackCapabilities]);

  const stopScanner = useCallback(
    (options?: { resetUi?: boolean }) => {
      const resetUi = options?.resetUi ?? false;

      if (scanTimerRef.current !== null) {
        window.clearInterval(scanTimerRef.current);
        scanTimerRef.current = null;
      }

      setIsScanning(false);
      setIsStarting(false);

      if (resetUi) {
        resetScannerUiState();
      }

      readerInstanceRef.current = null;
      stopTracks();
    },
    [resetScannerUiState, stopTracks],
  );

  const resolvePreferredCameraId = useCallback(async (): Promise<string | null> => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter((d) => d.kind === "videoinput");

      if (cameras.length === 0) return null;

      const hasLabels = cameras.some((c) => c.label.length > 0);
      if (!hasLabels) return null;

      const preferred = cameras.find((camera) =>
        BACK_CAMERA_LABEL_RE.test(camera.label),
      );
      if (preferred?.deviceId) return preferred.deviceId;

      if (cameras.length === 1 && cameras[0]?.deviceId) {
        return cameras[0].deviceId;
      }

      return null;
    } catch {
      return null;
    }
  }, []);

  const parsePath = useCallback((url: string) => {
    const normalizedUrl = url.replace(/\/+$/, "");
    const segments = normalizedUrl.split("/");
    const id = segments[segments.length - 1];

    let type: "part" | "asset" | "location" | "unknown" = "unknown";

    if (segments.includes("ersatzteile") || segments.includes("parts")) {
      type = "part";
    } else if (
      (segments.includes("maschinenverwaltung") &&
        segments.includes("maschine")) ||
      segments.includes("assets")
    ) {
      type = "asset";
    } else if (
      segments.includes("branches") ||
      segments.includes("standorte")
    ) {
      type = "location";
    }

    return { id, type };
  }, []);

  const onScanSuccess = useCallback(
    (decodedText: string) => {
      void (async () => {
        try {
          const normalizedText = normalizeScannedValue(decodedText);
          if (!normalizedText) {
            toast.error(t("scanner.notFound"));
            onOpenChange(false);
            return;
          }

          if (onScanResult) {
            onScanResult(normalizedText);
            onOpenChange(false);
            return;
          }

          if (looksLikeShortId(normalizedText)) {
            try {
              const { entity, oldId } = await shortIdsService.getByShortId(normalizedText);
              if (entity === "ASSET") {
                toast.success(t("scanner.assetFound", { name: "Asset" }));
                navigate(`/assets/${oldId}`);
                onOpenChange(false);
                return;
              }
              if (entity === "PART") {
                toast.success(t("scanner.partFound", { name: "Part" }));
                navigate(`/parts/${oldId}`);
                onOpenChange(false);
                return;
              }
              if (entity === "LOCATION") {
                toast.success(t("scanner.locationFound", { name: "Location" }));
                navigate(`/branches/${oldId}`);
                onOpenChange(false);
                return;
              }
            } catch {
              /* not a registered short id — fall through to URL / JSON handling */
            }
          }

          // Try JSON parsing
          try {
            const parsedData = JSON.parse(normalizedText);
            if (
              parsedData &&
              typeof parsedData === "object" &&
              parsedData.type &&
              parsedData.id
            ) {
              if (parsedData.type === "asset") {
                toast.success(t("scanner.assetFound", { name: parsedData.name || "Asset" }));
                navigate(`/assets/${parsedData.id}`);
                onOpenChange(false);
                return;
              }
              if (parsedData.type === "part") {
                toast.success(t("scanner.partFound", { name: parsedData.name || "Part" }));
                navigate(`/parts/${parsedData.id}`);
                onOpenChange(false);
                return;
              }
              if (parsedData.type === "location") {
                toast.success(t("scanner.locationFound", { name: parsedData.name || "Location" }));
                navigate(`/branches/${parsedData.id}`);
                onOpenChange(false);
                return;
              }
            }
          } catch {
            // not JSON — continue to URL parsing
          }

          const { id, type } = parsePath(normalizedText);
          if (type === "asset" && id) {
            toast.success(t("scanner.assetFound", { name: "Asset" }));
            navigate(`/assets/${id}`);
            onOpenChange(false);
            return;
          }
          if (type === "part" && id) {
            toast.success(t("scanner.partFound", { name: "Part" }));
            navigate(`/parts/${id}`);
            onOpenChange(false);
            return;
          }
          if (type === "location" && id) {
            toast.success(t("scanner.locationFound", { name: "Location" }));
            navigate(`/branches/${id}`);
            onOpenChange(false);
            return;
          }

          toast.error(t("scanner.notFound"));
          onOpenChange(false);
        } finally {
          handlingSuccessRef.current = false;
        }
      })();
    },
    [navigate, onOpenChange, onScanResult, parsePath, t],
  );

  onScanSuccessRef.current = onScanSuccess;

  const stopScannerRef = useRef(stopScanner);
  stopScannerRef.current = stopScanner;

  const handleDecodedValueRef = useRef<(text: string) => void>();
  handleDecodedValueRef.current = (decodedText: string) => {
    const normalizedText = normalizeScannedValue(decodedText);
    if (!normalizedText || handlingSuccessRef.current) return;

    handlingSuccessRef.current = true;
    stopScannerRef.current({ resetUi: false });
    onScanSuccessRef.current(normalizedText);
  };

  const applyBestTrackSettingsRef = useRef(applyBestTrackSettings);
  applyBestTrackSettingsRef.current = applyBestTrackSettings;

  const syncCameraSummaryRef = useRef(syncCameraSummary);
  syncCameraSummaryRef.current = syncCameraSummary;

  const syncTrackCapabilitiesRef = useRef(syncTrackCapabilities);
  syncTrackCapabilitiesRef.current = syncTrackCapabilities;

  const resolvePreferredCameraIdRef = useRef(resolvePreferredCameraId);
  resolvePreferredCameraIdRef.current = resolvePreferredCameraId;

  const startDecodeLoop = useCallback(() => {
    if (scanTimerRef.current !== null) {
      window.clearInterval(scanTimerRef.current);
      scanTimerRef.current = null;
    }

    const zxReader = createReader(modeRef.current);
    readerInstanceRef.current = zxReader;
    detectionRef.current = { value: null, count: 0, lastAt: 0 };

    if (!canvasRef.current) {
      canvasRef.current = document.createElement("canvas");
    }
    if (!ctxRef.current) {
      ctxRef.current = canvasRef.current.getContext("2d", { willReadFrequently: true });
    }

    const canvas = canvasRef.current!;
    const ctx = ctxRef.current!;
    const runKey = restartKeyRef.current;

    scanTimerRef.current = window.setInterval(() => {
      if (
        cancelledRef.current ||
        handlingSuccessRef.current ||
        runKey !== restartKeyRef.current
      ) {
        return;
      }

      const vid = videoRef.current;
      if (!vid || vid.readyState < vid.HAVE_CURRENT_DATA) return;

      for (const ratio of CROP_RATIOS) {
        const text = decodeCrop(zxReader, canvas, ctx, vid, ratio);
        if (!text) continue;

        const value = normalizeScannedValue(text);
        if (!value) continue;

        const now = Date.now();
        const current = detectionRef.current;

        if (
          current.value === value &&
          now - current.lastAt < DUPLICATE_RESULT_WINDOW_MS
        ) {
          current.count += 1;
          current.lastAt = now;
        } else {
          current.value = value;
          current.count = 1;
          current.lastAt = now;
        }

        if (current.count >= REQUIRED_MATCHES) {
          handleDecodedValueRef.current?.(value);
        }
        return;
      }
    }, SCAN_INTERVAL_MS);
  }, []);

  const startDecodeLoopRef = useRef(startDecodeLoop);
  startDecodeLoopRef.current = startDecodeLoop;

  useEffect(() => {
    const currentRun = ++restartKeyRef.current;

    const startScanner = async () => {
      if (!open || cancelledRef.current || !videoRef.current) return;

      setPermissionDenied(false);
      setError(null);
      setIsScanning(false);
      setIsStarting(true);
      handlingSuccessRef.current = false;

      try {
        const preferredCameraId = await resolvePreferredCameraIdRef.current();
        if (
          cancelledRef.current ||
          !videoRef.current ||
          currentRun !== restartKeyRef.current
        ) {
          return;
        }

        stopScannerRef.current({ resetUi: false });

        if (
          cancelledRef.current ||
          !videoRef.current ||
          currentRun !== restartKeyRef.current
        ) {
          return;
        }

        const videoEl = videoRef.current;
        videoEl.setAttribute("playsinline", "true");
        videoEl.setAttribute("muted", "true");
        videoEl.muted = true;

        let stream: MediaStream;
        try {
          stream = await navigator.mediaDevices.getUserMedia(
            buildVideoConstraints(preferredCameraId),
          );
        } catch (constraintErr) {
          if (preferredCameraId) {
            stream = await navigator.mediaDevices.getUserMedia(
              buildVideoConstraints(null, true),
            );
          } else {
            throw constraintErr;
          }
        }

        if (cancelledRef.current || currentRun !== restartKeyRef.current) {
          for (const track of stream.getTracks()) track.stop();
          return;
        }

        streamRef.current = stream;
        videoEl.srcObject = stream;
        await videoEl.play();

        await applyBestTrackSettingsRef.current();

        if (cancelledRef.current || currentRun !== restartKeyRef.current) {
          stopScannerRef.current({ resetUi: false });
          return;
        }

        startDecodeLoopRef.current();

        syncCameraSummaryRef.current();
        syncTrackCapabilitiesRef.current();
        setIsScanning(true);
      } catch (startupError) {
        stopScannerRef.current({ resetUi: false });

        const err = startupError as { name?: string; message?: string };
        if (
          err.name === "NotAllowedError" ||
          err.name === "PermissionDeniedError" ||
          err.message?.toLowerCase().includes("permission")
        ) {
          setPermissionDenied(true);
        } else {
          setError(
            t("scanner.error", {
              error: err.message || t("scanner.unknownError"),
            }),
          );
        }
      } finally {
        if (!cancelledRef.current && currentRun === restartKeyRef.current) {
          setIsStarting(false);
        }
      }
    };

    cancelledRef.current = false;

    if (open) {
      initTimeoutRef.current = window.setTimeout(() => {
        initTimeoutRef.current = null;
        void startScanner();
      }, CAMERA_INIT_DELAY_MS);
    } else {
      cancelledRef.current = true;
      stopScannerRef.current({ resetUi: true });
    }

    return () => {
      cancelledRef.current = true;

      if (initTimeoutRef.current !== null) {
        window.clearTimeout(initTimeoutRef.current);
        initTimeoutRef.current = null;
      }

      stopScannerRef.current({ resetUi: true });
    };
  }, [open, t]);

  const prevModeRef = useRef(mode);
  useEffect(() => {
    if (!isScanning || !streamRef.current) return;
    if (prevModeRef.current === mode) return;
    prevModeRef.current = mode;
    startDecodeLoopRef.current();
  }, [mode, isScanning]);

  const defaultInstruction =
    mode === "qr-only"
      ? t("scanner.instructionDefaultQr")
      : mode === "code128-only"
        ? t("scanner.instructionDefaultCode128")
        : t("scanner.instructionDefaultGeneric");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="left-0 top-0 flex h-[100dvh] min-h-[100dvh] max-h-[100dvh] w-screen min-w-full max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none border-0 bg-background p-0 shadow-none sm:left-[50%] sm:top-[50%] sm:h-auto sm:min-h-0 sm:max-h-[95dvh] sm:w-full sm:min-w-0 sm:max-w-[760px] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:gap-4 sm:rounded-2xl sm:border sm:p-6 sm:shadow-lg">
        <DialogHeader className="shrink-0 space-y-3 border-b bg-background/95 px-4 pb-3 pt-[max(1rem,env(safe-area-inset-top))] pr-12 backdrop-blur sm:border-b-0 sm:bg-transparent sm:px-0 sm:pb-3 sm:pt-0 sm:pr-12">
          <DialogTitle>{title ?? t("scanner.title")}</DialogTitle>
          {headerAccessory}
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden px-3 pb-[max(.75rem,env(safe-area-inset-bottom))] pt-3 sm:gap-4 sm:px-0 sm:pb-0 sm:pt-0">
          <div className="shrink-0 rounded-2xl border bg-muted/30 px-4 py-3 text-sm leading-relaxed text-muted-foreground">
            {instruction ?? defaultInstruction}
          </div>

          {permissionDenied ? (
            <Alert variant="destructive">
              <AlertDescription>{t("scanner.permissionDenied")}</AlertDescription>
            </Alert>
          ) : (
            <>
              {(isStarting || isScanning) && (
                <div className="shrink-0 space-y-3 rounded-2xl border bg-background/90 px-4 py-3 shadow-sm">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-green-500" />
                    <span>
                      {isStarting
                        ? t("scanner.starting")
                        : t("scanner.active")}
                    </span>
                  </div>

                  {(zoomSupported || hasEverShownZoom) ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>{t("scanner.zoom")}</span>
                        <span>{zoomLevel.toFixed(1)}x</span>
                      </div>
                      <input
                        type="range"
                        min={zoomRange.min}
                        max={zoomRange.max}
                        step={zoomRange.step}
                        value={zoomLevel}
                        onChange={(e) => void applyZoom(Number(e.target.value))}
                        className="w-full"
                      />
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground">
                      {t("scanner.zoomNotSupported")}
                    </div>
                  )}

                  {(torchSupported || hasEverShownTorch) && (
                    <div className="flex justify-start">
                      <Button
                        type="button"
                        variant={torchOn ? "default" : "outline"}
                        size="sm"
                        onClick={() => void applyTorch(!torchOn)}
                      >
                        {torchOn ? t("scanner.torchOn") : t("scanner.torchOff")}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              <div className="relative min-h-0 flex-1 overflow-hidden rounded-[1.5rem] border bg-black shadow-inner sm:min-h-[420px] sm:max-h-[72dvh]">
                <div
                  id={effectiveReaderId}
                  className="absolute inset-0 overflow-hidden"
                >
                  <video
                    ref={videoRef}
                    className="h-full w-full object-cover"
                    muted
                    playsInline
                    autoPlay
                  />
                </div>

                <div className="pointer-events-none absolute inset-3 rounded-[1.25rem] border border-white/20 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)] sm:inset-4">
                  <div className="absolute left-0 top-0 h-9 w-9 rounded-tl-[1.25rem] border-l-4 border-t-4 border-primary sm:h-10 sm:w-10" />
                  <div className="absolute right-0 top-0 h-9 w-9 rounded-tr-[1.25rem] border-r-4 border-t-4 border-primary sm:h-10 sm:w-10" />
                  <div className="absolute bottom-0 left-0 h-9 w-9 rounded-bl-[1.25rem] border-b-4 border-l-4 border-primary sm:h-10 sm:w-10" />
                  <div className="absolute bottom-0 right-0 h-9 w-9 rounded-br-[1.25rem] border-b-4 border-r-4 border-primary sm:h-10 sm:w-10" />
                </div>

                <div className="pointer-events-none absolute inset-x-3 bottom-3 rounded-2xl bg-black/65 px-4 py-2 text-center text-xs text-white/90 backdrop-blur-sm sm:inset-x-4 sm:bottom-4 sm:rounded-full">
                  {mode === "qr-only"
                    ? t("scanner.overlayHintQr")
                    : mode === "code128-only"
                      ? t("scanner.overlayHintCode128")
                      : t("scanner.overlayHintGeneric")}
                </div>
              </div>
            </>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
